import { useEffect, useRef, useState } from "react";
import {
  Box,
  Flex,
  Input,
  InputGroup,
  InputRightElement,
  List,
  ListItem,
  Tag,
  TagLabel,
  TagCloseButton,
  VStack,
  Wrap,
  useColorModeValue
} from "@chakra-ui/react";
import { SearchIcon } from "@chakra-ui/icons";
import { useTranslation } from "react-i18next";
import { FaCaretDown, FaCaretUp } from "react-icons/fa";

const getDisplayValue = (items, displayField) => {
  if (!items.length) return "";
  return items
    .map(item => {
      if (Array.isArray(displayField)) {
        return displayField.map(field => item?.[field] ?? "").join(" - ").trim();
      }
      return item?.[displayField] ?? "";
    })
    .join(", ");
};

const ProfileSearchSelect = ({
  id,
  items,
  displayField,
  valueField,
  placeholder,
  value = [],
  onChange,
  required
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [filteredItems, setFilteredItems] = useState(items);
  const wrapperRef = useRef(null);
  const { t } = useTranslation();
  const [selectedValues, setSelectedValues] = useState(value || []);

  const bgColor = useColorModeValue("white", "navy.900");
  const inputTextColor = useColorModeValue("gray.900", "white");
  const placeholderColor = useColorModeValue("gray.300", "gray.400");
  const borderColor = useColorModeValue("gray.200", "navy.700");
  const hoverColor = useColorModeValue("gray.100", "gray.600");
  const dropdownTextColor = useColorModeValue("gray.700", "gray.200");
  const tagColorScheme = useColorModeValue("gray", "white");

  useEffect(() => {
    if (isOpen) {
      setFilteredItems(items);
    }
  }, [isOpen, items]);

  useEffect(() => {
    setSelectedValues(value || []);
  }, [value]);

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearch(value);

    setFilteredItems(
      items.filter((item) => {
        if (Array.isArray(displayField)) {
          const concatenatedFields = displayField
            .map((field) => String(item[field] ?? ""))
            .join(" - ");

          return concatenatedFields.toLowerCase().includes(value.toLowerCase().trim());
        }

        return String(item[displayField] ?? "").toLowerCase().includes(value.toLowerCase().trim());
      })
    );
  };

  const changeEvent = ({ id, value }) => ({
    type: "change",
    persist: () => { },
    target: { id, value },
    currentTarget: { id, value },
  });

  const handleSelect = (item) => {
    const exists = selectedValues.some(selected => selected[valueField] === item[valueField]);
    const newSelection = exists
      ? selectedValues.filter(selected => selected[valueField] !== item[valueField])
      : [...selectedValues, item];

    setSelectedValues(newSelection);
    onChange(changeEvent({ id, value: newSelection }));
  };

  const handleRemoveTag = (item) => {
    const newSelection = selectedValues.filter(selected => selected[valueField] !== item[valueField]);
    setSelectedValues(newSelection);
    onChange(changeEvent({ id, value: newSelection }));
  };

  const handleClickOutside = (event) => {
    if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
      setIsOpen(false);
    }
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <VStack align="stretch" spacing={2} width="100%" ref={wrapperRef}>
      <Box
        border="1px solid"
        borderColor={borderColor}
        borderRadius="8px"
        boxShadow="sm"
        bg={bgColor}
        px={4}
        py={2}
        width="100%"
        onClick={() => setIsOpen(true)}
        cursor="pointer"
        minHeight="40px"
        gap={2}
      >
        <Flex color={placeholderColor} justify="space-between" align="center">
          <Box fontSize="sm">{placeholder}</Box>
          <Box>
            {isOpen ? (
              <FaCaretUp fontSize="sm" />
            ) : (
              <FaCaretDown fontSize="sm" />
            )}
          </Box>
        </Flex>

        <Wrap spacing={1} pt={1}>
          <>
            {selectedValues.map(item => (
              <Tag
                fontSize="sm"
                key={item[valueField]}
                size="lg"
                colorScheme={tagColorScheme}
                borderRadius="full"
              >
                <TagLabel>{getDisplayValue([item], displayField)}</TagLabel>
                <TagCloseButton
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRemoveTag(item);
                  }}
                />
              </Tag>
            ))}
          </>
        </Wrap>

        {isOpen && (
          <Box
            bg={bgColor}
            py={2}
            width="100%"
            zIndex="10"
            mt={2}
          >
            <InputGroup>
              <Input
                placeholder={t("system.placeholders.search")}
                value={search}
                bg={bgColor}
                onChange={handleSearchChange}
                mb={2}
                py="18px"
                borderColor={borderColor}
                fontSize="sm"
                height="28px"
                color={inputTextColor}
                _placeholder={{ color: placeholderColor, fontSize: "sm" }}
                onFocus={(e) => e.stopPropagation()}
                required={required}
              />
              <InputRightElement>
                <SearchIcon color={placeholderColor} />
              </InputRightElement>
            </InputGroup>

            <List maxHeight="200px" overflowY="auto" fontSize="sm" color={dropdownTextColor}>
              {filteredItems.length > 0 ? (
                filteredItems.map((item) => (
                  <ListItem
                    key={item[valueField]}
                    onClick={() => handleSelect(item)}
                    bg={selectedValues.some(selected => selected[valueField] === item[valueField])  ? useColorModeValue("blue.100", "navy.700") : bgColor}
                    padding="6px"
                    cursor="pointer"
                    _hover={{ bg: hoverColor }}
                  >
                    {getDisplayValue([item], displayField)}
                  </ListItem>
                ))
              ) : (
                <ListItem padding="6px" color={placeholderColor}>
                  {t("system.messages.info.no-data")}
                </ListItem>
              )}
            </List>
          </Box>
        )}
      </Box>
    </VStack >
  );
};

export default ProfileSearchSelect;